import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { SpecRenderer } from "@/features/apps/spec-renderer";
import type { DashboardSpec } from "@/features/apps/spec-schema";

export const Route = createFileRoute("/apps/demo")({
  head: () => ({
    meta: [
      { title: "App Builder demo — AskBell" },
      {
        name: "description",
        content: "Sample dashboard rendered by the spec engine.",
      },
    ],
  }),
  component: DemoPage,
});

/* ─────────────────── Sample dataset ─────────────────── */
const SAMPLE_ROWS = [
  { region: "Northeast", channel: "Direct", quarter: "Q1", revenue: 142000, deals: 18, won: true },
  { region: "Northeast", channel: "Partner", quarter: "Q1", revenue: 88000, deals: 11, won: true },
  { region: "Northeast", channel: "Direct", quarter: "Q2", revenue: 175000, deals: 22, won: true },
  { region: "Northeast", channel: "Partner", quarter: "Q2", revenue: 92000, deals: 12, won: false },
  { region: "Northeast", channel: "Direct", quarter: "Q3", revenue: 198000, deals: 25, won: true },
  { region: "Northeast", channel: "Partner", quarter: "Q3", revenue: 105000, deals: 14, won: true },
  { region: "Northeast", channel: "Direct", quarter: "Q4", revenue: 224000, deals: 28, won: true },
  { region: "Northeast", channel: "Partner", quarter: "Q4", revenue: 121000, deals: 16, won: true },
  { region: "Southeast", channel: "Direct", quarter: "Q1", revenue: 98000, deals: 14, won: true },
  { region: "Southeast", channel: "Partner", quarter: "Q1", revenue: 64000, deals: 9, won: false },
  { region: "Southeast", channel: "Direct", quarter: "Q2", revenue: 112000, deals: 16, won: true },
  { region: "Southeast", channel: "Partner", quarter: "Q2", revenue: 71000, deals: 10, won: true },
  { region: "Southeast", channel: "Direct", quarter: "Q3", revenue: 134000, deals: 19, won: true },
  { region: "Southeast", channel: "Partner", quarter: "Q3", revenue: 82000, deals: 12, won: false },
  { region: "Southeast", channel: "Direct", quarter: "Q4", revenue: 156000, deals: 21, won: true },
  { region: "Southeast", channel: "Partner", quarter: "Q4", revenue: 94000, deals: 13, won: true },
  { region: "Midwest", channel: "Direct", quarter: "Q1", revenue: 76000, deals: 11, won: true },
  { region: "Midwest", channel: "Partner", quarter: "Q1", revenue: 52000, deals: 8, won: true },
  { region: "Midwest", channel: "Direct", quarter: "Q2", revenue: 89000, deals: 13, won: false },
  { region: "Midwest", channel: "Partner", quarter: "Q2", revenue: 61000, deals: 9, won: true },
  { region: "Midwest", channel: "Direct", quarter: "Q3", revenue: 102000, deals: 15, won: true },
  { region: "Midwest", channel: "Partner", quarter: "Q3", revenue: 73000, deals: 11, won: true },
  { region: "Midwest", channel: "Direct", quarter: "Q4", revenue: 118000, deals: 17, won: true },
  { region: "Midwest", channel: "Partner", quarter: "Q4", revenue: 84000, deals: 12, won: true },
  { region: "West", channel: "Direct", quarter: "Q1", revenue: 165000, deals: 21, won: true },
  { region: "West", channel: "Partner", quarter: "Q1", revenue: 102000, deals: 14, won: true },
  { region: "West", channel: "Direct", quarter: "Q2", revenue: 188000, deals: 24, won: true },
  { region: "West", channel: "Partner", quarter: "Q2", revenue: 118000, deals: 16, won: false },
  { region: "West", channel: "Direct", quarter: "Q3", revenue: 215000, deals: 27, won: true },
  { region: "West", channel: "Partner", quarter: "Q3", revenue: 132000, deals: 18, won: true },
  { region: "West", channel: "Direct", quarter: "Q4", revenue: 248000, deals: 31, won: true },
  { region: "West", channel: "Partner", quarter: "Q4", revenue: 148000, deals: 20, won: true },
];

const DEMO_SPEC: DashboardSpec = {
  version: 1,
  title: "2024 Sales Performance",
  description: "Quarterly revenue and deal volume by region and channel.",
  datasets: [
    {
      id: "primary",
      label: "Sales 2024",
      columns: [
        { name: "region", type: "string" },
        { name: "channel", type: "string" },
        { name: "quarter", type: "string" },
        { name: "revenue", type: "number" },
        { name: "deals", type: "number" },
        { name: "won", type: "boolean" },
      ],
    },
  ],
  widgets: [
    {
      id: "kpi-revenue",
      type: "kpi",
      dataset_id: "primary",
      title: "Total Revenue",
      column: "revenue",
      aggregation: "sum",
      format: "currency",
      decimals: 0,
      width: 3,
    },
    {
      id: "kpi-deals",
      type: "kpi",
      dataset_id: "primary",
      title: "Total Deals",
      aggregation: "count",
      format: "number",
      decimals: 0,
      width: 3,
    },
    {
      id: "kpi-avg",
      type: "kpi",
      dataset_id: "primary",
      title: "Avg Deal Size",
      column: "revenue",
      aggregation: "avg",
      format: "currency",
      decimals: 0,
      width: 3,
    },
    {
      id: "kpi-regions",
      type: "kpi",
      dataset_id: "primary",
      title: "Active Regions",
      column: "region",
      aggregation: "count_distinct",
      format: "number",
      decimals: 0,
      width: 3,
    },
    {
      id: "section-trends",
      type: "section",
      dataset_id: "primary",
      heading: "Trends",
      width: 12,
    },
    {
      id: "trend-quarter",
      type: "line",
      dataset_id: "primary",
      title: "Revenue by Quarter",
      x: "quarter",
      series: [{ column: "revenue", aggregation: "sum", label: "Revenue" }],
      group_by: "region",
      sort: "asc",
      stacked: false,
      width: 8,
    },
    {
      id: "share-channel",
      type: "pie",
      dataset_id: "primary",
      title: "Revenue by Channel",
      category: "channel",
      value_column: "revenue",
      aggregation: "sum",
      donut: true,
      width: 4,
    },
    {
      id: "section-breakdown",
      type: "section",
      dataset_id: "primary",
      heading: "Breakdown",
      width: 12,
    },
    {
      id: "bar-region",
      type: "bar",
      dataset_id: "primary",
      title: "Revenue by Region",
      x: "region",
      series: [{ column: "revenue", aggregation: "sum", label: "Revenue" }],
      group_by: "channel",
      sort: "value_desc",
      stacked: true,
      width: 6,
    },
    {
      id: "bar-deals",
      type: "bar",
      dataset_id: "primary",
      title: "Deals by Region",
      x: "region",
      series: [{ column: "deals", aggregation: "sum", label: "Deals" }],
      sort: "value_desc",
      stacked: false,
      width: 6,
    },
    {
      id: "table-detail",
      type: "table",
      dataset_id: "primary",
      title: "Detail",
      columns: [
        { column: "region" },
        { column: "channel" },
        { column: "quarter" },
        { column: "revenue", format: "currency" },
        { column: "deals", format: "number" },
        { column: "won" },
      ],
      page_size: 10,
      default_sort: { column: "revenue", direction: "desc" },
      width: 12,
    },
  ],
};

function DemoPage() {
  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 mb-6 -ml-2">
            <Link to="/apps">
              <ArrowLeft className="h-4 w-4" />
              Back to apps
            </Link>
          </Button>

          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
              Spec renderer demo
            </h1>
          </div>
          <p className="text-ui-base text-muted-foreground mb-6">
            A hardcoded sample dashboard rendered by the SpecRenderer to validate
            widget layouts, formatting, and chart styling end-to-end.
          </p>

          <SpecRenderer
            spec={DEMO_SPEC}
            datasets={{ primary: SAMPLE_ROWS }}
          />
        </div>
      </div>
    </AppLayout>
  );
}
